import { useEffect } from "react";

/**
 * Store listener hook with automatic cleanup
 * @param {Object|Array<Object>} actions - An array or single Reflux action(s) (ex: UIActions.showTheme)
 * @param {Function|Array<Function>} callbacks - Callback function(s) to call when the action is triggered
 * @param {Array} dependencies - Array of dependencies
 * @param {Object} options - Additional options:
 *   isCleanup - unsubscribe when component unmounts (default: true)
 *
 * @example
 * // Single action
 * useStoreListener(UIActions.showTheme, () => console.log('Theme shown'));
 *
 * // Multiple actions
 * useStoreListener(
 *   [UIActions.showTheme, UIActions.toggleActiveTheme],
 *   [() => console.log('Theme shown'),() => console.log('Theme toggled')],
 *   [someDependency]
 * );
 */
const useStoreListener = (actions, callbacks, dependencies = [], options = {}) => {
  const { isCleanup = true } = options;
  const _actions = Array.isArray(actions) ? actions : [actions];
  const _callbacks = Array.isArray(callbacks) ? callbacks : [callbacks];

  useEffect(() => {
    if (_actions.length !== _callbacks.length) {
      console.error("Actions and callbacks arrays must have the same length");
      return () => {}; // Return empty cleanup function
    }

    const unsubscribers = _actions.map((action, index) => {
      if (!action || typeof action.listen !== "function") {
        console.error(`Action index (${index}) missing listen method`);
        return () => {};
      }
      return action.listen(_callbacks[index]);
    });

    // cleanup all the subscriptions
    return () => {
      if (isCleanup) {
        unsubscribers.forEach((unsubscribe) => unsubscribe());
      }
    };
  }, dependencies);
};

export default useStoreListener;
